import React from "react";
import { format } from "date-fns";
import { shuffleGameData } from "../../lib/game-helpers";
import GameGrid from "../GameGrid";
import NumberOfMistakesDisplay from "../NumberMistakes";
import NoPuzzle from "../NoPuzzle";

import { Separator } from "../ui/separator";

import { PuzzleDataContext } from "../../providers/PuzzleData";
import { GameStatusContext } from "../../providers/GameStatus";
import GameControlButtonsPanel from "../GameButtons";

import ViewResultsModal from "../modals/ViewResultsModal";
import { ProgressContext } from "../../providers/Progress";
import { SPORT_LABELS } from "../../lib/constants";

function Game() {
    const { gameData, isLoading, puzzleDate, puzzleId, sport, noPuzzle } =
        React.useContext(PuzzleDataContext);
    const {
        submittedGuesses,
        solvedGameData,
        isGameOver,
        isGameWon,
        numMistakesUsed,
    } = React.useContext(GameStatusContext);
    const { recordResult } = React.useContext(ProgressContext);

    const [shuffledRows, setShuffledRows] = React.useState([]);
    const [gridShake, setGridShake] = React.useState(false);

    const hasLongWords = React.useMemo(() => {
        return shuffledRows.flat().some((word) => word.length >= 10);
    }, [shuffledRows]);

    const containerMaxWidth = hasLongWords ? "max-w-[1000px]" : "max-w-[800px]";

    // Track previous values to detect what changed
    const prevGameDataRef = React.useRef(gameData);
    const prevSolvedCountRef = React.useRef(solvedGameData.length);

    // Combined effect to handle both initial load and solved row updates
    React.useEffect(() => {
        if (!gameData.length) {
            setShuffledRows([]);
            return;
        }

        const gameDataChanged = prevGameDataRef.current !== gameData;
        const solvedCountChanged = prevSolvedCountRef.current !== solvedGameData.length;

        // Update refs
        prevGameDataRef.current = gameData;
        prevSolvedCountRef.current = solvedGameData.length;

        // Filter out solved categories
        const categoriesToRemove = solvedGameData.map((data) => data.category);
        const dataLeftForRows = gameData.filter(
            (data) => !categoriesToRemove.includes(data.category)
        );

        // Only shuffle if:
        // 1. Game data changed (new puzzle loaded) - always shuffle
        // 2. A new category was solved - shuffle remaining
        if (gameDataChanged || solvedCountChanged) {
            if (dataLeftForRows.length > 0) {
                setShuffledRows(shuffleGameData({ gameData: dataLeftForRows }));
            } else if (gameDataChanged) {
                // New puzzle with no solved data yet
                setShuffledRows(shuffleGameData({ gameData }));
            }
        }
    }, [gameData, solvedGameData, puzzleId, sport]);

    React.useEffect(() => {
        if (!isGameOver || !puzzleDate) return;
        recordResult({
            puzzleId: puzzleId || `${sport}-${format(puzzleDate, "yyyyMMdd")}`,
            puzzleDate: format(puzzleDate, "yyyy-MM-dd"),
            sport,
            result: isGameWon ? "win" : "loss",
            mistakes: numMistakesUsed,
            guesses: submittedGuesses.length,
        });
    }, [
        isGameOver,
        isGameWon,
        numMistakesUsed,
        puzzleDate,
        puzzleId,
        recordResult,
        sport,
        submittedGuesses.length,
    ]);



    if (isLoading) {
        return (
            <div className="game-wrapper animate-pulse">
                <div className="h-4 bg-slate-700/40 rounded w-2/3 mx-auto mb-4" />
                <div className="space-y-3">
                    <div className="h-16 rounded bg-slate-700/30" />
                    <div className="h-16 rounded bg-slate-700/20" />
                    <div className="h-16 rounded bg-slate-700/30" />
                </div>
            </div>
        );
    }

    // Show NoPuzzle component when no puzzle exists for the date
    if (noPuzzle || !gameData.length) {
        return <NoPuzzle />;
    }

    const sportLabel = SPORT_LABELS[sport] ?? sport?.toUpperCase();
    const subtitle = `${sportLabel} daily`;



    return (
        <div className={`w-full ${containerMaxWidth} px-4 sm:px-6 transition-all duration-300 ease-in-out`}>
            <div className="text-center mb-2 sm:mb-3">
                <p className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-1">
                    {subtitle}
                </p>
                <h3 className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold text-white leading-tight px-4">
                    Create Connected Groups
                </h3>
                <p className="text-slate-400 mt-1 text-sm sm:text-base font-medium px-4">
                    Select groups of 2 to 4 items
                </p>
            </div>

            <div className={`game-wrapper`}>

                <GameGrid
                    gameRows={shuffledRows}
                    shouldGridShake={gridShake}
                    setShouldGridShake={setGridShake}
                />
                <Separator className="my-2 bg-slate-700/30" />

                {!isGameOver ? (
                    <>
                        <NumberOfMistakesDisplay />
                        <GameControlButtonsPanel
                            shuffledRows={shuffledRows}
                            setShuffledRows={setShuffledRows}
                            setGridShake={setGridShake}
                        />
                    </>
                ) : (
                    <ViewResultsModal />
                )}
            </div>
        </div>
    );
}

export default Game;
